// ══════════════════════════════════════════════════════
// Trade-Analysis Panel (Phase 2a)
// Shared component für alle 3 Run-Typen (opt/wf/bt).
// Rendert Picker (wenn opt/wf), Summary, Equity, Exit-Reasons.
// ══════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from "react";
import { theme, fonts } from "../theme";
import { API_BASE } from "../config";
import { SummaryMetrics, TradeEquityCurve, ExitReasonsBar, HourWeekdayHeatmap, MaeMfeScatter } from "./TradeCharts";
import TradeTable from "./TradeTable";
import TradeInsights from "./TradeInsights";

// Kleiner Inline-Card-Ersatz: Die bestehenden Card-Primitives importieren hier
// zu viele Kontexte. Bleibt kompakt & self-contained.
const SubCard = ({ title, children, style }) => (
  <div style={{
    background: theme.white,
    border: `1px solid ${theme.border}`,
    borderRadius: 12,
    padding: "14px 18px",
    ...style,
  }}>
    {title && <div style={{
      fontSize: 11, fontWeight: 700, color: theme.text2,
      textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 12,
    }}>{title}</div>}
    {children}
  </div>
);

// Banner für "no_trade_data" / 410-Responses
const EmptyBanner = ({ msg, hint }) => (
  <div style={{
    padding: "20px 24px",
    background: theme.amberLight,
    border: `1px solid rgba(217,119,6,0.2)`,
    borderRadius: 12,
    fontSize: 13,
    color: theme.amberText,
  }}>
    <div style={{ fontWeight: 700, marginBottom: 4 }}>ℹ️ {msg}</div>
    {hint && <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>{hint}</div>}
  </div>
);

const LoadingSkeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    <div style={{ height: 100, background: theme.tint, borderRadius: 10 }} className="sl-shimmer" />
    <div style={{ height: 220, background: theme.tint, borderRadius: 10 }} className="sl-shimmer" />
    <div style={{ height: 160, background: theme.tint, borderRadius: 10 }} className="sl-shimmer" />
  </div>
);

// ─── Trial-Picker (nur Opt) ──────────────────────────────
function TrialPicker({ trials, selectedId, onChange }) {
  if (!trials || trials.length === 0) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
      <label style={{ fontSize: 11, color: theme.text3, fontWeight: 700,
                      textTransform: "uppercase", letterSpacing: "0.3px" }}>
        Trial:
      </label>
      <select
        value={selectedId ?? ""}
        onChange={e => onChange(Number(e.target.value))}
        style={{
          padding: "6px 10px", fontSize: 12, fontFamily: fonts.mono,
          border: `1px solid ${theme.border}`, borderRadius: 7,
          background: theme.white, color: theme.text, cursor: "pointer",
          minWidth: 320,
        }}
      >
        {trials.map(t => {
          const pnl = t.net_pnl != null ? `$${Math.round(t.net_pnl)}` : "—";
          const grade = t.grade || "?";
          const wr = t.win_rate != null ? `${Number(t.win_rate).toFixed(0)}%` : "—";
          const pf = t.profit_factor != null ? `PF ${Number(t.profit_factor).toFixed(2)}` : "";
          return (
            <option key={t.trial} value={t.trial}>
              {t.is_best ? "★ " : ""}Trial #{t.trial} — Grade {grade} · {pnl} · {wr} · {pf} · {t.trade_count || 0} trades
            </option>
          );
        })}
      </select>
    </div>
  );
}

// ─── Window+Segment-Picker (nur WF) ──────────────────────
function WindowPicker({ windows, window, onWindowChange, segment, onSegmentChange }) {
  if (!windows || windows.length === 0) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12,
                  marginBottom: 14, flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <label style={{ fontSize: 11, color: theme.text3, fontWeight: 700,
                        textTransform: "uppercase", letterSpacing: "0.3px" }}>
          Window:
        </label>
        <select
          value={window}
          onChange={e => onWindowChange(Number(e.target.value))}
          style={{
            padding: "6px 10px", fontSize: 12, fontFamily: fonts.mono,
            border: `1px solid ${theme.border}`, borderRadius: 7,
            background: theme.white, color: theme.text, cursor: "pointer",
            minWidth: 260,
          }}
        >
          {windows.map(w => (
            <option key={w.window} value={w.window}>
              #{w.window} · IS {w.is_start}→{w.is_end} / OOS {w.oos_start}→{w.oos_end}
            </option>
          ))}
        </select>
      </div>
      {/* IS/OOS Toggle */}
      <div style={{ display: "flex", gap: 3, padding: 3, borderRadius: 7,
                    background: theme.tint }}>
        {["oos", "is"].map(s => (
          <button key={s}
            onClick={() => onSegmentChange(s)}
            style={{
              padding: "4px 12px", borderRadius: 5, border: "none", cursor: "pointer",
              fontSize: 11, fontWeight: segment === s ? 700 : 500,
              background: segment === s ? theme.white : "transparent",
              color: segment === s ? theme.text : theme.text3,
              boxShadow: segment === s ? theme.shadow1 : "none",
              textTransform: "uppercase",
            }}>
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// MAIN PANEL
// Props:
//   runType: "opt" | "wf" | "bt"
//   runId:   number
// ═════════════════════════════════════════════════════════
export default function TradeAnalysisPanel({ runType, runId }) {
  // Trial-Summary für Opt (List von Trials)
  const [trialsSummary, setTrialsSummary] = useState(null);
  const [selectedTrial, setSelectedTrial] = useState(null);

  // Window+Segment für WF
  const [windows, setWindows] = useState(null);
  const [selectedWindow, setSelectedWindow] = useState(1);
  const [selectedSegment, setSelectedSegment] = useState("oos");

  // Data für aktuell ausgewähltes Segment
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noData, setNoData] = useState(null);  // {msg, hint} bei 410/no_trade_data

  // ── Initial-Load: für Opt erst trials_summary, für WF die Windows aus dem Run-Result ──
  useEffect(() => {
    if (!runId || !runType) return;
    setError(null);
    setNoData(null);
    setData(null);
    setTrialsSummary(null);
    setWindows(null);

    if (runType === "opt") {
      setLoading(true);
      fetch(`${API_BASE}/analyze/opt/${runId}/trials_summary`)
        .then(r => r.json())
        .then(j => {
          if (j.no_trade_data) {
            setNoData({
              msg: "Keine Trade-Daten persistiert",
              hint: j.reason || "Run wurde vor dem Trade-Level-Deploy erstellt. Für vollständige Analyse einen neuen Optimizer-Lauf starten.",
            });
            setLoading(false);
            return;
          }
          setTrialsSummary(j);
          const defaultId = j.best_trial ?? (j.trials?.[0]?.trial);
          if (defaultId != null) setSelectedTrial(defaultId);
          else setLoading(false);
        })
        .catch(e => { setError(e.message); setLoading(false); });
    } else if (runType === "wf") {
      // Windows holen wir aus dem bereits geladenen WF-Run-Result des Parent
      // Aber der Parent gibt uns keinen Callback — also lesen wir sie aus einer
      // eigenen minimalen API-Call (wir brauchen nur die Windows-Liste mit Dates).
      setLoading(true);
      fetch(`${API_BASE}/results/walkforwards/${runId}`)
        .then(r => r.json())
        .then(j => {
          const wins = j?.windows || [];
          if (!wins.length) {
            setNoData({ msg: "Keine Windows in diesem WF-Run" });
            setLoading(false);
            return;
          }
          // Metadaten-only-Mapping (kein trades_list hier — das kommt per Window-Fetch)
          setWindows(wins.map(w => ({
            window: w.window, is_start: w.is_start, is_end: w.is_end,
            oos_start: w.oos_start, oos_end: w.oos_end,
          })));
          setSelectedWindow(1);
        })
        .catch(e => { setError(e.message); setLoading(false); });
    } else if (runType === "bt") {
      // Backtest: direkt laden, kein Picker
      loadBt();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, runType]);

  // ── Load-Helper für Backtest ──
  const loadBt = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/analyze/bt/${runId}/trades`)
      .then(r => { if (r.status === 410) throw new Error("410"); return r.json(); })
      .then(j => { setData(j); setLoading(false); })
      .catch(e => {
        if (String(e.message) === "410") {
          setNoData({ msg: "Keine Trade-Daten persistiert",
                      hint: "Alter Backtest ohne MAE/MFE-Felder." });
        } else {
          setError(e.message);
        }
        setLoading(false);
      });
  }, [runId]);

  // ── Opt: wenn ein Trial ausgewählt ist, Trades laden ──
  useEffect(() => {
    if (runType !== "opt" || selectedTrial == null || !trialsSummary) return;
    setLoading(true);
    fetch(`${API_BASE}/analyze/opt/${runId}/trades?trial_id=${selectedTrial}`)
      .then(r => {
        if (r.status === 410 || r.status === 404) throw new Error(String(r.status));
        return r.json();
      })
      .then(j => { setData(j); setLoading(false); })
      .catch(e => {
        if (e.message === "410" || e.message === "404") {
          setNoData({
            msg: "Trade-Details für diesen Trial nicht persistiert",
            hint: "Nur die Top-Trials nach jeder Metrik haben persistierte Detail-Daten. Wähle einen anderen Trial aus der Liste oder starte einen neuen Optimizer-Lauf.",
          });
        } else setError(e.message);
        setLoading(false);
      });
  }, [runType, runId, selectedTrial, trialsSummary]);

  // ── WF: wenn Window+Segment ausgewählt, Trades laden ──
  useEffect(() => {
    if (runType !== "wf" || !windows || !selectedWindow) return;
    setLoading(true);
    fetch(`${API_BASE}/analyze/wf/${runId}/window/${selectedWindow}/trades?segment=${selectedSegment}`)
      .then(r => { if (r.status === 410) throw new Error("410"); return r.json(); })
      .then(j => { setData(j); setLoading(false); })
      .catch(e => {
        if (String(e.message) === "410") {
          setNoData({ msg: "Keine Trade-Daten für dieses Window",
                      hint: "WF-Run pre-dates Phase-1b — neuen Walk-Forward starten für Trade-Level-Analyse." });
        } else setError(e.message);
        setLoading(false);
      });
  }, [runType, runId, selectedWindow, selectedSegment, windows]);

  // ── Render ──────────────────────────────────────────────

  // Fehler-State
  if (error) {
    return <EmptyBanner msg={`Fehler beim Laden: ${error}`} hint="Prüfe die Browser-Konsole für Details." />;
  }

  // Wenn KEIN trialsSummary/windows da ist UND noData → Banner als Full-Replacement
  // (alter Run pre-Phase-1, gar keine Trade-Daten möglich)
  const hasPicker = runType === "opt" ? trialsSummary?.trials?.length > 0
                   : runType === "wf" ? windows?.length > 0
                   : runType === "bt";
  if (noData && !hasPicker) {
    return <EmptyBanner msg={noData.msg} hint={noData.hint} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* ── Picker-Zeile (bleibt sichtbar damit User anderen Trial wählen kann) ── */}
      {runType === "opt" && trialsSummary?.trials && (
        <TrialPicker
          trials={trialsSummary.trials}
          selectedId={selectedTrial}
          onChange={(id) => { setNoData(null); setSelectedTrial(id); }}
        />
      )}
      {runType === "wf" && windows && (
        <WindowPicker
          windows={windows}
          window={selectedWindow}
          onWindowChange={(w) => { setNoData(null); setSelectedWindow(w); }}
          segment={selectedSegment}
          onSegmentChange={(s) => { setNoData(null); setSelectedSegment(s); }}
        />
      )}

      {/* ── Inline-Banner für ausgewählten Trial ohne Details ── */}
      {noData && hasPicker && (
        <EmptyBanner msg={noData.msg} hint={noData.hint} />
      )}

      {/* ── Content ── */}
      {loading && <LoadingSkeleton />}

      {!loading && !noData && data?.stats && (
        <>
          {/* Summary */}
          <SubCard title="Kennzahlen">
            <SummaryMetrics summary={data.stats.summary} />
          </SubCard>

          {/* Rule-based Interpretation — oben damit direkt sichtbar was aus den Daten folgt */}
          <SubCard title="📋 Erkenntnisse & Interpretation">
            <TradeInsights stats={data.stats} trades={data.trades} />
          </SubCard>

          {/* Equity + Exit-Reasons nebeneinander bei viel Platz, untereinander bei wenig */}
          <div style={{ display: "grid",
                        gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
                        gap: 14 }}
               className="trade-analysis-grid">
            <SubCard title={`Equity-Kurve · ${data.trades?.length ?? 0} Trades`}>
              <TradeEquityCurve points={data.stats.equity_curve} />
            </SubCard>
            <SubCard title="Exit-Reasons">
              <ExitReasonsBar reasons={data.stats.exit_reasons} />
            </SubCard>
          </div>

          {/* Hour × Weekday Heatmap (full-width — braucht breiten Platz für 24 Stunden-Spalten) */}
          <SubCard title="Hour × Weekday Heatmap · Entry-Zeit">
            <HourWeekdayHeatmap heatmap={data.stats.heatmap} />
          </SubCard>

          {/* MAE / MFE Scatter — volle Breite, da quadratisch gut im Karten-Layout */}
          <SubCard title="MAE / MFE Scatter · Entry-Qualität">
            <MaeMfeScatter pairs={data.stats.mae_mfe_pairs} W={860} H={360} />
          </SubCard>

          {/* Trade-Tabelle mit Sort/Filter/Pagination/CSV-Export */}
          <SubCard title="Trade-Liste · sortier- & filterbar">
            <TradeTable
              trades={data.trades}
              filename={
                runType === "opt" ? `opt_${runId}_trial_${data.trial_id}_trades`
                : runType === "wf" ? `wf_${runId}_w${data.window}_${data.segment}_trades`
                : `bt_${runId}_trades`
              }
              meta={
                runType === "opt" ? `Trial #${data.trial_id}`
                : runType === "wf" ? `Window ${data.window} · ${data.segment?.toUpperCase()}`
                : null
              }
              vizContext={
                data.instrument ? {
                  instrument: data.instrument,
                  entry_tf:   data.timeframes?.entry   || "M5",
                  context_tf: data.timeframes?.context || "H1",
                  regime_tf:  data.timeframes?.regime  || "H4",
                } : null
              }
            />
          </SubCard>

          {/* Footer mit Meta-Info für WF */}
          {runType === "wf" && data.window_meta && (
            <div style={{ fontSize: 10, color: theme.text4, fontFamily: fonts.mono,
                          padding: "6px 2px" }}>
              Window {data.window}: IS {data.window_meta.is_start}→{data.window_meta.is_end} · OOS {data.window_meta.oos_start}→{data.window_meta.oos_end} · Segment: {data.segment.toUpperCase()}
            </div>
          )}
        </>
      )}

      {!loading && !data && !hasPicker && (
        <EmptyBanner msg="Noch keine Daten — Picker wählen oder Daten werden geladen…" />
      )}
    </div>
  );
}
